import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetClose, SheetTitle } from '@/components/ui/sheet';
import { Edit, Eye, FileText, User, Calendar, Loader2, X } from 'lucide-react';
import SmartPagination from '../ui/smart-pagination';
import { PaginationParams } from '@/lib/types';
import NoteFormDrawer from '../clinic/NoteFormDrawer';
import { EmrClinicalNote } from '@/services/emrService';
import { useClinic } from '@/contexts/ClinicContext';

interface PatientSOAPNotesTabProps {
  isLoading: boolean;
  notes: EmrClinicalNote[];
  pagination: PaginationParams;
  onPaginationChange: (page: number) => void;
  refetch: () => void;
}

const PatientSOAPNotesTab = ({ isLoading, notes, pagination, onPaginationChange, refetch }: PatientSOAPNotesTabProps) => {
  const { isPractitioner, isAdmin } = useClinic();
  const [selectedNote, setSelectedNote] = useState<EmrClinicalNote | null>(null);
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [isNoteDrawerOpen, setIsNoteDrawerOpen] = useState(false);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getPractitionerName = (note: EmrClinicalNote) => {
    if (note.practitioner) {
      return `Dr. ${note.practitioner.first_name} ${note.practitioner.last_name}`;
    }
    return 'Unknown Practitioner';
  };

  const truncateText = (text: string, maxLength: number = 80) => {
    if (!text) return '—';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const handleViewDetails = (note: EmrClinicalNote) => {
    setSelectedNote(note);
    setIsSheetOpen(true);
  };

  const handleEditNote = (note: EmrClinicalNote) => {
    setSelectedNote(note);
    setIsNoteDrawerOpen(true);
  };

  const handleCloseSheet = () => {
    setIsSheetOpen(false);
    setTimeout(() => setSelectedNote(null), 300);
  };

  return (
    <>
      <div className="space-y-6">
        {/* Main Card */}
        <div className="bg-white rounded-lg border border-slate-200">
          {/* Header */}
          <div className="px-5 py-4 border-b border-slate-100">
            <div className="flex items-center gap-3">
              <div
                className="w-9 h-9 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: '#6366f1' }}
              >
                <FileText className="h-4 w-4 text-white" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-slate-900">SOAP Notes</h3>
                <p className="text-xs text-slate-500">
                  {isLoading ? 'Loading...' : `${pagination.totalItems || notes.length} total notes`}
                </p>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-5">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-[#6366f1]" />
                <span className="ml-2 text-sm text-slate-500">Loading SOAP notes...</span>
              </div>
            ) : notes.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-3">
                  <FileText className="h-6 w-6 text-slate-400" />
                </div>
                <p className="text-sm text-slate-500">No SOAP notes available</p>
              </div>
            ) : (
              <div className="space-y-3">
                {notes.map((note) => (
                  <div
                    key={note.id}
                    className="border border-slate-200 rounded-lg p-4 bg-white"
                  >
                    {/* Header Row */}
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-3">
                      <div className="flex items-center gap-3">
                        <div
                          className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                          style={{ backgroundColor: 'rgba(99, 57, 144, 0.15)' }}
                        >
                          <User className="h-5 w-5" style={{ color: 'rgb(99, 57, 144)' }} />
                        </div>
                        <div>
                          <div className="font-medium text-slate-900 text-sm">
                            {getPractitionerName(note)}
                          </div>
                          <div className="flex items-center gap-1 text-xs text-slate-500 mt-0.5">
                            <Calendar className="h-3 w-3" />
                            {formatDate(note.created_at)}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 flex-shrink-0">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleViewDetails(note)}
                          className="h-8 px-3 text-xs"
                        >
                          <Eye className="h-3.5 w-3.5 mr-1" />
                          View
                        </Button>
                        {(isPractitioner || isAdmin) && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEditNote(note)}
                            className="h-8 px-3 text-xs"
                          >
                            <Edit className="h-3.5 w-3.5 mr-1" />
                            Edit
                          </Button>
                        )}
                      </div>
                    </div>

                    {/* SOAP Content Preview */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                      <div className="flex gap-2">
                        <span className="text-xs font-bold text-blue-600 bg-blue-50 px-2 py-0.5 rounded h-fit">S</span>
                        <p className="text-xs text-slate-600 leading-relaxed">{truncateText(note.subjective)}</p>
                      </div>
                      <div className="flex gap-2">
                        <span className="text-xs font-bold text-green-600 bg-green-50 px-2 py-0.5 rounded h-fit">O</span>
                        <p className="text-xs text-slate-600 leading-relaxed">{truncateText(note.objective)}</p>
                      </div>
                      <div className="flex gap-2">
                        <span className="text-xs font-bold text-amber-600 bg-amber-50 px-2 py-0.5 rounded h-fit">A</span>
                        <p className="text-xs text-slate-600 leading-relaxed">{truncateText(note.assessment)}</p>
                      </div>
                      <div className="flex gap-2">
                        <span className="text-xs font-bold text-purple-600 bg-purple-50 px-2 py-0.5 rounded h-fit">P</span>
                        <p className="text-xs text-slate-600 leading-relaxed">{truncateText(note.plan)}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Pagination Footer */}
          {pagination.totalPages > 1 && (
            <div className="px-5 py-4 border-t border-slate-100">
              <SmartPagination
                currentPage={pagination.page}
                totalPages={pagination.totalPages}
                onPageChange={onPaginationChange}
              />
            </div>
          )}
        </div>
      </div>

      {/* Edit Note Drawer */}
      {isNoteDrawerOpen && selectedNote && (
        <NoteFormDrawer
          open={isNoteDrawerOpen}
          onOpenChange={setIsNoteDrawerOpen}
          patientId={selectedNote.patient_id!}
          onSubmit={refetch}
          note={selectedNote}
        />
      )}

      {/* SOAP Note Details Sheet */}
      <Sheet open={isSheetOpen} onOpenChange={handleCloseSheet}>
        <SheetContent className="w-full !max-w-[540px] rounded-l-2xl p-0 flex flex-col gap-0 [&>button]:hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
            <SheetTitle className="font-semibold text-slate-900" style={{ fontSize: '1.05rem' }}>
              SOAP Note Details
            </SheetTitle>
            <SheetClose asChild>
              <button className="w-10 h-10 rounded-full border border-slate-200 flex items-center justify-center hover:bg-slate-50 transition-colors">
                <X className="h-5 w-5 text-slate-600" />
              </button>
            </SheetClose>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto px-6 py-6">
            {selectedNote && (
              <div className="space-y-6">
                {/* Practitioner and Date Info */}
                <div className="pb-5 border-b border-slate-200">
                  <div className="flex items-center gap-3">
                    <div className="w-11 h-11 rounded-lg flex items-center justify-center bg-purple-100">
                      <User className="h-5 w-5 text-purple-600" />
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-slate-900">
                        {getPractitionerName(selectedNote)}
                      </div>
                      <div className="flex items-center gap-1.5 text-xs text-slate-500 mt-0.5">
                        <Calendar className="h-3 w-3" />
                        {formatDate(selectedNote.created_at)}
                      </div>
                    </div>
                  </div>
                </div>

                {/* SOAP Sections */}
                <div className="space-y-4">
                  {/* Subjective */}
                  <div className="rounded-lg p-4 border border-slate-200">
                    <div className="flex items-center gap-2.5 mb-3">
                      <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
                        <span className="text-sm font-bold text-blue-600">S</span>
                      </div>
                      <h3 className="text-sm font-semibold text-slate-900">Subjective</h3>
                    </div>
                    <p className="text-sm text-slate-600 leading-relaxed whitespace-pre-wrap">
                      {selectedNote.subjective || '—'}
                    </p>
                  </div>

                  {/* Objective */}
                  <div className="rounded-lg p-4 border border-slate-200">
                    <div className="flex items-center gap-2.5 mb-3">
                      <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center">
                        <span className="text-sm font-bold text-emerald-600">O</span>
                      </div>
                      <h3 className="text-sm font-semibold text-slate-900">Objective</h3>
                    </div>
                    <p className="text-sm text-slate-600 leading-relaxed whitespace-pre-wrap">
                      {selectedNote.objective || '—'}
                    </p>
                  </div>

                  {/* Assessment */}
                  <div className="rounded-lg p-4 border border-slate-200">
                    <div className="flex items-center gap-2.5 mb-3">
                      <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center">
                        <span className="text-sm font-bold text-amber-600">A</span>
                      </div>
                      <h3 className="text-sm font-semibold text-slate-900">Assessment</h3>
                    </div>
                    <p className="text-sm text-slate-600 leading-relaxed whitespace-pre-wrap">
                      {selectedNote.assessment || '—'}
                    </p>
                  </div>

                  {/* Plan */}
                  <div className="rounded-lg p-4 border border-slate-200">
                    <div className="flex items-center gap-2.5 mb-3">
                      <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center">
                        <span className="text-sm font-bold text-purple-600">P</span>
                      </div>
                      <h3 className="text-sm font-semibold text-slate-900">Plan</h3>
                    </div>
                    <p className="text-sm text-slate-600 leading-relaxed whitespace-pre-wrap">
                      {selectedNote.plan || '—'}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-end">
            <Button
              variant="outline"
              onClick={handleCloseSheet}
              className="rounded-lg"
            >
              Close
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
};

export default PatientSOAPNotesTab;
